/**
 * CCH Tabbed Pages — Grouped views for Community Care Hub
 *
 * 3 pages, each with tabs that embed the individual CCH module components:
 *
 * 1. Care Management: Care Plans, Authorizations, CHW Supervision, NEMT, Housing & 211, Discharge
 * 2. Hub Administration: Credentialing, Grievances, Accreditation, Funds Flow
 * 3. Reporting & Outcomes: Sponsor Console, Quality Measures, Outcomes
 */

import React, { useState } from 'react';
import CCHModulePage from './CCHModulePage';

// Shared tab bar component
function TabBar({ tabs, activeTab, onTabChange }: {
  tabs: { id: string; label: string }[];
  activeTab: string;
  onTabChange: (id: string) => void;
}) {
  return (
    <div className="border-b border-gray-200 mb-4">
      <nav className="flex gap-0 -mb-px overflow-x-auto" aria-label="Tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`whitespace-nowrap py-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

// ── Care Management Page ──────────────────────────────────────────────────────

const CARE_TABS = [
  { id: 'cch-care-plans', label: 'Care Plans' },
  { id: 'cch-authorizations', label: 'Authorizations' },
  { id: 'cch-chw-supervision', label: 'CHW Supervision' },
  { id: 'cch-nemt', label: 'NEMT' },
  { id: 'cch-housing', label: 'Housing & 211' },
  { id: 'cch-discharge-planning', label: 'Discharge' },
];

export function CCHCareManagementPage({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const [activeTab, setActiveTab] = useState('cch-care-plans');
  return (
    <div>
      <TabBar tabs={CARE_TABS} activeTab={activeTab} onTabChange={setActiveTab} />
      <CCHModulePage module={activeTab} onNavigate={onNavigate} />
    </div>
  );
}

// ── Hub Administration Page ───────────────────────────────────────────────────

const ADMIN_TABS = [
  { id: 'cch-credentialing', label: 'Credentialing' },
  { id: 'cch-grievances', label: 'Grievances & Appeals' },
  { id: 'cch-accreditation', label: 'Accreditation' },
  { id: 'cch-funds-flow', label: 'Funds Flow' },
];

export function CCHHubAdminPage({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const [activeTab, setActiveTab] = useState('cch-credentialing');
  return (
    <div>
      <TabBar tabs={ADMIN_TABS} activeTab={activeTab} onTabChange={setActiveTab} />
      <CCHModulePage module={activeTab} onNavigate={onNavigate} />
    </div>
  );
}

// ── Reporting & Outcomes Page ─────────────────────────────────────────────────

const REPORTING_TABS = [
  { id: 'cch-sponsor-console', label: 'Sponsor Console' },
  { id: 'cch-quality-measures', label: 'Quality Measures' },
  { id: 'cch-outcomes', label: 'Outcomes' },
];

export function CCHReportingPage({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const [activeTab, setActiveTab] = useState('cch-sponsor-console');
  return (
    <div>
      <TabBar tabs={REPORTING_TABS} activeTab={activeTab} onTabChange={setActiveTab} />
      <CCHModulePage module={activeTab} onNavigate={onNavigate} />
    </div>
  );
}

